/**
 * Sugestões — painel administrativo
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Select, FormControl, InputLabel,
  Avatar, Tooltip, IconButton,
} from '@mui/material';
import LightbulbIcon    from '@mui/icons-material/Lightbulb';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import OpenInNewIcon    from '@mui/icons-material/OpenInNew';
import { listarTodasAdmin, atualizarSugestao } from '../../services/sugestaoService';

const ORANGE = '#F97316';
const GREEN  = '#10B981';
const BLUE   = '#2563EB';
const AMBER  = '#F59E0B';
const PURPLE = '#7C3AED';
const RED    = '#EF4444';
const DARK   = '#0F172A';

const STATUS_MAP = {
  pendente:     { label: 'Recebida',      color: AMBER,  icon: <HourglassTopIcon sx={{ fontSize: 12 }} /> },
  em_analise:   { label: 'Em análise',    color: BLUE,   icon: <HourglassTopIcon sx={{ fontSize: 12 }} /> },
  planejado:    { label: 'Planejado',     color: PURPLE, icon: <RocketLaunchIcon sx={{ fontSize: 12 }} /> },
  implementado: { label: 'Implementado',  color: GREEN,  icon: <CheckCircleIcon  sx={{ fontSize: 12 }} /> },
  recusado:     { label: 'Não aplicável', color: RED,    icon: null },
};

const CATEGORIAS = {
  melhoria:       '✨ Melhoria',
  funcionalidade: '🆕 Funcionalidade',
  bug:            '🐛 Correção',
  outro:          '💬 Outro',
};

const STATUS_OPTIONS = [
  { value: 'pendente',     label: 'Recebida'       },
  { value: 'em_analise',   label: 'Em análise'     },
  { value: 'planejado',    label: 'Planejado'      },
  { value: 'implementado', label: 'Implementado'   },
  { value: 'recusado',     label: 'Não aplicável'  },
];

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';

const TH = ({ children, align = 'left' }) => (
  <TableCell align={align} sx={{
    fontSize: 10, fontWeight: 700, color: '#94A3B8',
    letterSpacing: '0.07em', textTransform: 'uppercase',
    borderBottom: '2px solid #F1F5F9', bgcolor: '#fff',
  }}>{children}</TableCell>
);

const TD = ({ children, align = 'left', sx: sxProp }) => (
  <TableCell align={align} sx={{ fontSize: 13, color: '#475569', py: 1.5, ...sxProp }}>
    {children}
  </TableCell>
);

export default function SugestoesAdmin() {
  const [sugestoes, setSugestoes]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filtroStatus, setFiltro]   = useState('');
  const [modal, setModal]           = useState(null); // sugestão selecionada
  const [novoStatus, setNovoStatus] = useState('');
  const [resposta, setResposta]     = useState('');
  const [salvando, setSalvando]     = useState(false);

  const carregar = async (status = '') => {
    setLoading(true);
    try {
      const res = await listarTodasAdmin(status);
      setSugestoes(res.data || []);
    } catch { /* silencioso */ }
    setLoading(false);
  };

  useEffect(() => { carregar(filtroStatus); }, [filtroStatus]);

  const abrirModal = (s) => {
    setModal(s);
    setNovoStatus(s.status);
    setResposta(s.respostaAdmin || '');
  };

  const fecharModal = () => setModal(null);

  const handleSalvar = async () => {
    if (!modal) return;
    setSalvando(true);
    try {
      await atualizarSugestao(modal.id, { status: novoStatus, respostaAdmin: resposta });
      await carregar(filtroStatus);
      fecharModal();
    } catch { /* silencioso */ }
    setSalvando(false);
  };

  // Contagens por status
  const counts = sugestoes.reduce((acc, s) => {
    acc[s.status] = (acc[s.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <Box sx={{ p: 3 }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Avatar sx={{ width: 44, height: 44, bgcolor: `${ORANGE}15` }}>
          <LightbulbIcon sx={{ color: ORANGE, fontSize: 22 }} />
        </Avatar>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: DARK }}>
            Sugestões dos Dentistas
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8' }}>
            Analise e responda as ideias enviadas pelos usuários
          </Typography>
        </Box>
      </Box>

      {/* ── KPIs rápidos ── */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        {[
          { key: 'pendente',     label: 'Recebidas',   color: AMBER  },
          { key: 'em_analise',   label: 'Em análise',  color: BLUE   },
          { key: 'planejado',    label: 'Planejadas',  color: PURPLE },
          { key: 'implementado', label: 'Entregues',   color: GREEN  },
        ].map(k => (
          <Card key={k.key} elevation={0} sx={{
            flex: '1 1 120px', borderRadius: 2.5,
            border: `1px solid ${k.color}25`, bgcolor: `${k.color}08`,
            cursor: 'pointer',
            outline: filtroStatus === k.key ? `2px solid ${k.color}` : 'none',
          }} onClick={() => setFiltro(filtroStatus === k.key ? '' : k.key)}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, color: k.color, lineHeight: 1 }}>
                {counts[k.key] || 0}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontWeight: 600 }}>
                {k.label}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* ── Filtro ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Filtrar por status</InputLabel>
          <Select value={filtroStatus} label="Filtrar por status" onChange={e => setFiltro(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {STATUS_OPTIONS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
          </Select>
        </FormControl>
        <Typography sx={{ fontSize: '0.82rem', color: '#94A3B8' }}>
          {sugestoes.length} sugestão{sugestoes.length !== 1 ? 'ões' : ''}
        </Typography>
      </Box>

      {/* ── Tabela ── */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress sx={{ color: ORANGE }} />
        </Box>
      ) : sugestoes.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, color: '#CBD5E1' }}>
          <LightbulbIcon sx={{ fontSize: 48, mb: 1 }} />
          <Typography sx={{ fontWeight: 600 }}>Nenhuma sugestão encontrada</Typography>
        </Box>
      ) : (
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TH>Data</TH>
                  <TH>Dentista</TH>
                  <TH>Título</TH>
                  <TH>Categoria</TH>
                  <TH align="center">Status</TH>
                  <TH align="center">Ação</TH>
                </TableRow>
              </TableHead>
              <TableBody>
                {sugestoes.map(s => {
                  const st = STATUS_MAP[s.status] || STATUS_MAP.pendente;
                  return (
                    <TableRow key={s.id} sx={{ '&:hover': { bgcolor: '#F8FAFC' } }}>
                      <TD sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDate(s.createdAt)}</TD>
                      <TD>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: DARK }}>
                          {s.usuario?.nome || '—'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8' }}>
                          {s.clinica?.nome}
                        </Typography>
                      </TD>
                      <TD>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: DARK }}>
                          {s.titulo}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280 }}>
                          {s.descricao}
                        </Typography>
                      </TD>
                      <TD sx={{ fontSize: 12 }}>{CATEGORIAS[s.categoria] || s.categoria}</TD>
                      <TD align="center">
                        <Chip
                          label={st.label} size="small" icon={st.icon}
                          sx={{
                            bgcolor: `${st.color}15`, color: st.color,
                            border: `1px solid ${st.color}30`,
                            fontWeight: 700, fontSize: '0.68rem',
                            '& .MuiChip-icon': { color: st.color },
                          }}
                        />
                      </TD>
                      <TD align="center">
                        <Tooltip title="Ver e responder">
                          <IconButton size="small" onClick={() => abrirModal(s)}
                            sx={{ color: ORANGE, '&:hover': { bgcolor: `${ORANGE}12` } }}>
                            <OpenInNewIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      </TD>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* ── Modal de resposta ── */}
      <Dialog open={!!modal} onClose={fecharModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ borderBottom: '1px solid #F1F5F9', pb: 2 }}>
          <Typography fontWeight={700} fontSize="1rem" color={DARK}>
            Analisar sugestão
          </Typography>
          {modal && (
            <Typography fontSize="0.78rem" color="#94A3B8" mt={0.3}>
              de {modal.usuario?.nome} · {fmtDate(modal?.createdAt)}
            </Typography>
          )}
        </DialogTitle>

        {modal && (
          <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#94A3B8', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Título
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: DARK }}>{modal.titulo}</Typography>
            </Box>

            <Box sx={{ bgcolor: '#F8FAFC', borderRadius: 2, p: 2, border: '1px solid #F1F5F9' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#94A3B8', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Descrição
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: '#334155', lineHeight: 1.7 }}>
                {modal.descricao}
              </Typography>
            </Box>

            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={novoStatus} label="Status" onChange={e => setNovoStatus(e.target.value)}>
                {STATUS_OPTIONS.map(s => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Resposta para o dentista (opcional)"
              multiline minRows={3} fullWidth size="small"
              value={resposta}
              onChange={e => setResposta(e.target.value)}
              placeholder="Explique o que será feito, ou por que não será implementado..."
            />
          </DialogContent>
        )}

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={fecharModal} sx={{ textTransform: 'none', color: '#64748B' }}>
            Cancelar
          </Button>
          <Button
            variant="contained" onClick={handleSalvar} disabled={salvando}
            startIcon={salvando ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
            sx={{
              bgcolor: ORANGE, '&:hover': { bgcolor: '#EA580C' },
              textTransform: 'none', fontWeight: 700, borderRadius: 2,
              boxShadow: 'none',
            }}
          >
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
